// Оболочка в окне: PwaProvider. SPEC pwa-shell §4 (часть 3: оболочка в окне).
//
// Связывает платформенный слой и app-ui: регистрирует SW, ведёт жизненный цикл обновления,
// установку, разрешение/подписку на push, badge, навигацию по deep-link из клика пуша. Доменного
// состояния НЕ держит (владеет лишь PWA-оболочкой) — число для badge и маршрут навигации приходят
// снаружи. Все платформенные API инъецируются (`PwaEnvironment`) — провайдер не лезет в window
// напрямую, поэтому собирается/тестируется без браузера; реальные привязки даёт app-ui при монтаже.
import { applyBadge } from './badge.js';
import { InstallController } from './install.js';
import { UpdateController } from './update.js';
import { onNotificationClick } from './notification.js';
import { canOfferPush, subscribeToPush, unsubscribeFromPush, } from './push-subscription.js';
/**
 * Координатор PWA-оболочки в окне. Тонкий: вся доменная логика — в чистых модулях, здесь — связка
 * и проброс наружу флагов/действий, которые потребляет app-ui (через React-контекст в реальном
 * монтаже).
 */
export class PwaProvider {
    env;
    install = new InstallController();
    update;
    constructor(env) {
        this.env = env;
        this.update = new UpdateController();
    }
    // ── установка (§4.7) ──
    onBeforeInstallPrompt(event) {
        this.install.onBeforeInstallPrompt(event);
    }
    onAppInstalled() {
        this.install.onAppInstalled();
    }
    get canInstall() {
        return this.install.canInstall;
    }
    promptInstall() {
        return this.install.promptInstall();
    }
    // ── обновление SW (§4.3) ──
    onSwWaiting(reg) {
        this.update.onWaiting(reg);
    }
    get updateReady() {
        return this.update.updateReady;
    }
    applyUpdate() {
        this.update.applyUpdate();
    }
    /** На controllerchange — один reload через инъецированный reload (§4.3 п.4, guard внутри). */
    onControllerChange() {
        this.update.onControllerChange(this.env.reload);
    }
    // ── push (§4.7 / §4.8) ──
    get pushSupported() {
        return this.env.pushEnv.pushSupported;
    }
    get isStandalone() {
        return this.env.pushEnv.isStandalone;
    }
    get pushPermission() {
        return this.env.pushEnv.permission;
    }
    /** Можно ли предлагать подписку в этой среде (инв.4, §4.8) — для UI настроек. */
    get pushOffer() {
        return canOfferPush(this.env.pushEnv);
    }
    /** Включить пуши (по жесту пользователя): полный subscribe-флоу через client-transport. */
    enablePush() {
        return subscribeToPush({
            env: this.env.pushEnv,
            requestPermission: this.env.requestPermission,
            pushManager: this.env.pushManager,
            transport: this.env.transport,
        });
    }
    /** Выключить пуши: снять подписку и на сервере, и локально. */
    disablePush() {
        return unsubscribeFromPush({ pushManager: this.env.pushManager, transport: this.env.transport });
    }
    /** Есть ли активная браузерная подписка на push на этом устройстве (для UI «включено/выключено»). */
    async isPushSubscribed() {
        const sub = await this.env.pushManager.getSubscription();
        return sub !== null;
    }
    // ── badge (§4.6, инв.3) ──
    /** Отразить суммарные непрочитанные на иконке (число абсолютное; деградация без API). */
    setBadge(unreadTotal) {
        return applyBadge(this.env.badgeApi, unreadTotal);
    }
    // ── deep-link из клика пуша (§4.5, инв.1) ──
    /** Обработать SW→окно сообщение: navigate-команду роутером app-ui к deepLink. */
    onSwMessage(message) {
        if (message.type === 'navigate') {
            this.env.navigate(message.deepLink);
        }
    }
    /**
     * Прямой разбор нажатого уведомления (если окно само его получает) → навигация к сообщению.
     * Делегирует чистой onNotificationClick (носитель инв.1): всегда ведёт к deep-link, не к корню
     * «вместо» сообщения.
     */
    navigateFromNotification(notification) {
        this.env.navigate(onNotificationClick(notification));
    }
}
//# sourceMappingURL=pwa-provider.js.map